import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { AlertTriangle, Check, ExternalLink, Pencil, X } from "lucide-react";
import {
  bulkReviewSubmissions,
  fetchSubmissionFields,
  fetchSubmissionQueue,
  publishSubmission,
  rejectSubmission,
  updateSubmission,
} from "../../api/gearSubmissions";
import { useMediaQuery } from "react-responsive";
import { GEAR_PAGE_PATH } from "../../utils/gearSubmission";
import { getBorder, getMutedText, HEADING_FONT } from "../../constants/colors";

const BUTTON_SX = {
  borderRadius: "9px",
  fontFamily: HEADING_FONT,
  fontWeight: 600,
  textTransform: "none",
};

const STATUSES = ["pending", "published", "rejected"];

const itemLabel = (data) => [data.brand, data.name, data.variant].filter(Boolean).join(" ");

const DuplicateWarning = ({ duplicate, gearType }) => {
  if (!duplicate) return null;
  if (duplicate.kind === "catalog") {
    return (
      <Chip
        size="small"
        color="warning"
        icon={<AlertTriangle size={14} />}
        label={
          <Link
            href={`/gear/${GEAR_PAGE_PATH[gearType]}/${duplicate.itemId}`}
            target="_blank"
            color="inherit"
          >
            Already in catalog: {duplicate.label}
          </Link>
        }
      />
    );
  }
  return (
    <Chip size="small" color="warning" icon={<AlertTriangle size={14} />} label="Also pending in another submission" />
  );
};

const SubmissionCard = ({
  darkMode,
  submission,
  fields,
  selected,
  onToggleSelect,
  onChanged,
  addToAutocomplete,
  canAddToAutocomplete,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 898 });
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(submission.data);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const isPending = submission.status === "pending";

  const run = async (action) => {
    setBusy(true);
    setError("");
    const result = await action();
    setBusy(false);
    if (!result.ok) {
      setError(result.error || "Something went wrong");
      return false;
    }
    onChanged();
    return true;
  };

  const handleSave = async () => {
    const saved = await run(() => updateSubmission(submission._id, draft));
    if (saved) setEditing(false);
  };

  const filledFields = fields.filter(
    (field) => submission.data[field.key] !== undefined && submission.data[field.key] !== ""
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "14px",
        border: `1px solid ${selected ? "#1976d2" : getBorder(darkMode)}`,
        backgroundImage: "none",
        padding: isMobile ? "16px" : "20px 24px",
      }}
    >
      <Stack gap="12px">
        <Stack direction="row" alignItems="flex-start" gap="8px">
          {isPending && (
            <Checkbox size="small" checked={selected} onChange={onToggleSelect} sx={{ padding: "2px" }} />
          )}
          <Stack sx={{ flex: 1, minWidth: 0 }} gap="4px">
            <Typography sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: "16px" }}>
              {itemLabel(submission.data)}
            </Typography>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: "12px", color: getMutedText(darkMode) }}>
              Submitted by {submission.submittedByUsername || submission.submittedBy} on{" "}
              {new Date(submission.createdAt).toLocaleString()} via{" "}
              {submission.source === "csv" ? "CSV upload" : "form"}
              {submission.reviewedAt && ` · reviewed ${new Date(submission.reviewedAt).toLocaleString()}`}
            </Typography>
            <Stack direction="row" gap="6px" flexWrap="wrap">
              <DuplicateWarning duplicate={submission.possibleDuplicateOf} gearType={submission.gearType} />
              {submission.status === "published" && submission.publishedItemId && (
                <Chip
                  size="small"
                  color="success"
                  label={
                    <Link
                      href={`/gear/${GEAR_PAGE_PATH[submission.gearType]}/${submission.publishedItemId}`}
                      target="_blank"
                      color="inherit"
                    >
                      View published item
                    </Link>
                  }
                />
              )}
              {submission.reviewNote && <Chip size="small" label={`Note: ${submission.reviewNote}`} />}
            </Stack>
          </Stack>
          {submission.data.url && (
            <Button
              size="small"
              href={submission.data.url}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<ExternalLink size={14} />}
              sx={BUTTON_SX}
            >
              Source
            </Button>
          )}
        </Stack>

        {submission.submitterNote && (
          <Typography sx={{ fontSize: "13px", fontStyle: "italic", color: getMutedText(darkMode) }}>
            "{submission.submitterNote}"
          </Typography>
        )}

        {editing ? (
          <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(3, minmax(0, 1fr))", gap: "12px" }}>
            {fields.map((field) =>
              field.type === "enum" ? (
                <TextField
                  key={field.key}
                  select
                  size="small"
                  label={field.label}
                  value={draft[field.key] ?? ""}
                  onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Not specified</em>
                  </MenuItem>
                  {field.values.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  key={field.key}
                  size="small"
                  label={field.label}
                  multiline={field.type === "text"}
                  value={draft[field.key] ?? ""}
                  onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                />
              )
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
              gap: "6px 16px",
            }}
          >
            {filledFields.map((field) => (
              <Box key={field.key} sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "10.5px",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: getMutedText(darkMode),
                    fontFamily: HEADING_FONT,
                    fontWeight: 600,
                  }}
                >
                  {field.label}
                </Typography>
                <Typography sx={{ fontSize: "13px", wordBreak: "break-word" }}>
                  {field.type === "url" ? (
                    <Link href={submission.data[field.key]} target="_blank" rel="noopener noreferrer">
                      {field.key === "imageUrl" ? "Open image" : "Open link"}
                    </Link>
                  ) : (
                    String(submission.data[field.key])
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {submission.data.imageUrl && !editing && (
          <Box
            component="img"
            src={submission.data.imageUrl}
            alt=""
            sx={{ maxHeight: "120px", maxWidth: "200px", objectFit: "contain", borderRadius: "8px" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {isPending && (
          <Stack direction="row" gap="8px" flexWrap="wrap">
            {editing ? (
              <>
                <Button variant="contained" disableElevation size="small" disabled={busy} onClick={handleSave} sx={BUTTON_SX}>
                  Save changes
                </Button>
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => {
                    setDraft(submission.data);
                    setEditing(false);
                  }}
                  sx={BUTTON_SX}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  disableElevation
                  color="success"
                  size="small"
                  disabled={busy}
                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Check size={16} />}
                  onClick={() =>
                    run(() => publishSubmission(submission._id, addToAutocomplete && canAddToAutocomplete))
                  }
                  sx={BUTTON_SX}
                >
                  Publish
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={busy}
                  startIcon={<X size={16} />}
                  onClick={() => setRejectOpen(true)}
                  sx={BUTTON_SX}
                >
                  Reject
                </Button>
                <Button size="small" disabled={busy} startIcon={<Pencil size={14} />} onClick={() => setEditing(true)} sx={BUTTON_SX}>
                  Edit
                </Button>
              </>
            )}
          </Stack>
        )}
      </Stack>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontFamily: HEADING_FONT }}>Reject submission</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason (shown to the submitter)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ marginTop: "8px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} sx={BUTTON_SX}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={async () => {
              const rejected = await run(() => rejectSubmission(submission._id, reason));
              if (rejected) setRejectOpen(false);
            }}
            sx={BUTTON_SX}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const GearSubmissionQueue = ({ darkMode }) => {
  const isMobile = useMediaQuery({ maxWidth: 898 });
  const [config, setConfig] = React.useState(null);
  const [status, setStatus] = React.useState("pending");
  const [gearType, setGearType] = React.useState("");
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [selected, setSelected] = React.useState(new Set());
  const [addToAutocomplete, setAddToAutocomplete] = React.useState(true);
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const [bulkMessage, setBulkMessage] = React.useState("");

  const load = React.useCallback(async () => {
    const result = await fetchSubmissionQueue({ status, gearType });
    if (!result.ok) {
      setError(result.error || "Failed to load submissions");
      setData({ submissions: [], counts: {} });
      return;
    }
    setError("");
    setData(result);
    setSelected(new Set());
  }, [status, gearType]);

  React.useEffect(() => {
    (async () => {
      const result = await fetchSubmissionFields();
      if (result.ok) setConfig(result);
    })();
  }, []);

  React.useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const fieldsFor = (type) => {
    const definition = config && config.gearTypes.find((entry) => entry.key === type);
    return definition ? definition.fields : [];
  };
  const canAutocomplete = (type) => {
    const definition = config && config.gearTypes.find((entry) => entry.key === type);
    return Boolean(definition && definition.canAddToAutocomplete);
  };

  const handleBulk = async (action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt(`Reason for rejecting ${selected.size} submissions (shown to submitters):`, "");
      if (reason === null) return;
    }
    setBulkBusy(true);
    const result = await bulkReviewSubmissions({ ids: [...selected], action, reason, addToAutocomplete });
    setBulkBusy(false);
    if (!result.ok) {
      setBulkMessage(result.error || "Bulk action failed");
      return;
    }
    const failed = result.results.filter((entry) => !entry.ok);
    setBulkMessage(
      `${result.results.length - failed.length} ${action === "publish" ? "published" : "rejected"}` +
        (failed.length ? `, ${failed.length} failed: ${failed[0].error}` : "")
    );
    load();
  };

  const submissions = (data && data.submissions) || [];
  const counts = (data && data.counts) || {};

  return (
    <Stack sx={{ width: "100%", maxWidth: "1100px", margin: "0 auto", padding: "24px 16px" }} gap="16px">
      <Typography variant="h4" sx={{ fontFamily: HEADING_FONT, fontWeight: 800, letterSpacing: "-0.02em" }}>
        Gear submissions
      </Typography>
      <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "stretch" : "center"} gap="12px">
        <Tabs value={status} onChange={(e, value) => setStatus(value)}>
          {STATUSES.map((value) => (
            <Tab
              key={value}
              value={value}
              label={`${value} (${counts[value] || 0})`}
              sx={{ ...BUTTON_SX, textTransform: "capitalize" }}
            />
          ))}
        </Tabs>
        <TextField
          select
          size="small"
          label="Gear type"
          value={gearType}
          onChange={(e) => setGearType(e.target.value)}
          sx={{ minWidth: "200px" }}
        >
          <MenuItem value="">All gear types</MenuItem>
          {config &&
            config.gearTypes.map((type) => (
              <MenuItem key={type.key} value={type.key}>
                {type.label}
              </MenuItem>
            ))}
        </TextField>
      </Stack>

      {status === "pending" && (
        <Stack direction="row" alignItems="center" gap="10px" flexWrap="wrap">
          <FormControlLabel
            control={<Checkbox checked={addToAutocomplete} onChange={(e) => setAddToAutocomplete(e.target.checked)} />}
            label="Also add published items to pack builder autocomplete"
          />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            onClick={() =>
              setSelected(selected.size === submissions.length ? new Set() : new Set(submissions.map((s) => s._id)))
            }
            disabled={!submissions.length}
            sx={BUTTON_SX}
          >
            {selected.size === submissions.length && submissions.length ? "Clear selection" : "Select all"}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="success"
            disableElevation
            disabled={!selected.size || bulkBusy}
            onClick={() => handleBulk("publish")}
            sx={BUTTON_SX}
          >
            Publish {selected.size || ""} selected
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={!selected.size || bulkBusy}
            onClick={() => handleBulk("reject")}
            sx={BUTTON_SX}
          >
            Reject {selected.size || ""} selected
          </Button>
        </Stack>
      )}

      {bulkMessage && (
        <Alert severity="info" onClose={() => setBulkMessage("")}>
          {bulkMessage}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      {!data || !config ? (
        <Stack alignItems="center" sx={{ padding: "40px" }}>
          <CircularProgress />
        </Stack>
      ) : submissions.length === 0 ? (
        <Typography sx={{ fontFamily: HEADING_FONT, color: getMutedText(darkMode), padding: "24px 0" }}>
          No {status} submissions.
        </Typography>
      ) : (
        submissions.map((submission) => (
          <SubmissionCard
            key={`${submission._id}-${submission.updatedAt}`}
            darkMode={darkMode}
            submission={submission}
            fields={fieldsFor(submission.gearType)}
            selected={selected.has(submission._id)}
            onToggleSelect={() => {
              const next = new Set(selected);
              if (next.has(submission._id)) next.delete(submission._id);
              else next.add(submission._id);
              setSelected(next);
            }}
            onChanged={load}
            addToAutocomplete={addToAutocomplete}
            canAddToAutocomplete={canAutocomplete(submission.gearType)}
          />
        ))
      )}
    </Stack>
  );
};

export default GearSubmissionQueue;
